import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";
import { useMetricData } from "../useMetricData";
import { useMetricQuery } from "../useMetricQuery";
import { useSetAtom } from "jotai";
import { selectedPeriodAtom } from "../metrics.state";
import { labelingTableTheme } from "@/features/Labeling/components/FileView/labelingTableTheme";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { Chart } from "@/ui/organisms/Chart/Chart";
import { Axis } from "@/demos/CreditAnalystDemo/pages/CreditAnalystDemo.Home/components/Axis";
import clsx from "clsx";

export function MetricTable() {
    const { tablePeriods } = useMetricData();
    const { currentMetricQuery } = useMetricQuery();
    const gridRef = useRef<AgGridReact>(null);
    const setSelectedPeriod = useSetAtom(selectedPeriodAtom);
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);

    if (!tablePeriods) return null;

    return (
        <div className={clsx("flex min-h-0 w-full flex-1 flex-col gap-5 pt-4")}>
            <div className="flex h-[92px] flex-col gap-2">
                <AgGridReact
                    key={currentMetricQuery.metricQuery}
                    ref={gridRef}
                    theme={labelingTableTheme}
                    rowData={[{}]}
                    columnDefs={
                        tablePeriods.map(({ period, value }) => {
                            const formattedPeriod = period
                                .split(/<\d+>/)
                                .map((date) =>
                                    date.split("-").reverse().join("/"),
                                )
                                .join(" - ");
                            return {
                                field: period,
                                headerName: formattedPeriod,
                                valueGetter: () => {
                                    return value;
                                },
                                valueFormatter: () => {
                                    return value
                                        ? new Intl.NumberFormat("en-US", {
                                              style: "currency",
                                              currency: "USD",
                                          }).format(value)
                                        : "";
                                },
                                resizable: true,
                                sortable: false,
                                minWidth: Math.max(
                                    100,
                                    formattedPeriod.length * 10,
                                ),
                                suppressHeaderMenuButton: true,
                            };
                        }) || []
                    }
                    onCellClicked={(params) => {
                        console.log(params);
                        setRightDrawerOpen(true);
                        setSelectedPeriod(params.colDef.field as string);
                    }}
                    defaultColDef={{
                        flex: 1,
                    }}
                    rowSelection={{
                        mode: "multiRow",
                        checkboxes: false,
                        headerCheckbox: false,
                        enableClickSelection: true,
                    }}
                    domLayout="autoHeight"
                />
            </div>
            <div className="min-h-0 flex-1">
                <div className="size-full [&>div>div]:!pb-8 [&>div]:!h-full">
                    <Chart
                        title=""
                        description=""
                        isCondensed={false}
                        options={{
                            data: tablePeriods.map(({ period, value }) => ({
                                period: period
                                    .split(/<\d+>/)
                                    .map((date) =>
                                        date.split("-").reverse().join("/"),
                                    )
                                    .join(" - "),
                                realPeriod: period,
                                value,
                            })),
                            series: [
                                {
                                    type: "bar",
                                    xKey: "period",
                                    yKey: "value",
                                    yName: "EBITDA",
                                },
                            ],
                            axes: [
                                Axis.Category({
                                    position: "bottom",
                                    label: {
                                        rotation:
                                            tablePeriods[0]?.period.length >
                                                8 || tablePeriods.length > 12
                                                ? 60
                                                : 0,
                                        avoidCollisions: true,
                                    },
                                }),
                                Axis.Money({
                                    keys: ["value"],
                                }),
                            ],
                            listeners: {
                                seriesNodeClick: ({
                                    datum,
                                    yKey,
                                    seriesId,
                                }) => {
                                    console.log(datum, yKey, seriesId);
                                    setSelectedPeriod(datum.realPeriod);
                                    setRightDrawerOpen(true);
                                },
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
